import type { HelpCategory } from "../../types";

export const stock: HelpCategory = {
  id: "stock",
  title: "Склад и закупки",
  description: "Остатки, закупки, списания, инвентаризация, поставщики, несколько складов, перемещения, сроки годности, разделка и производство.",
  icon: "warehouse",
  articles: [
    {
      id: "stock-balances",
      title: "Остатки на складе и карточка движения",
      summary: "Сколько продуктов на складе и на какую сумму, почему остаток уходит в минус и как посмотреть всю историю движения продукта.",
      path: "/inventory",
      feature: "core.inventory_basic",
      permission: "stock.view",
      keywords: ["остатки", "склад", "минус", "fifo", "fefo", "партия", "движение", "стоимость остатков"],
      related: ["purchases", "writeoffs", "inventory-counts", "warehouses"],
      body: [
        { t: "p", text: "[«Остатки»](/inventory) показывают каждый продукт: количество, минимум, стоимость и состояние — **«В норме»**, **«Мало»** (ниже минимума) или **«В минусе»**. Нажмите на строку — откроется **карточка движения**: остаток на начало, все приходы и расходы по типам и остаток на конец за выбранный период." },
        {
          t: "terms", title: "Как считается стоимость",
          items: [
            { term: "Партии", text: "Каждая закупка — отдельная партия со своей ценой (и сроком годности, если указан)." },
            { term: "FIFO", text: "Расход берётся из самой ранней партии. Так себестоимость продажи — по реальной цене той закупки." },
            { term: "FEFO", text: "С расширенным складом — сначала партия с ближайшим сроком годности. См. [[expiry]]." },
          ],
        },
        { t: "h", text: "Почему остаток в минусе" },
        { t: "p", text: "Продажа никогда не блокируется из-за склада: если продали больше, чем было по учёту (забыли провести закупку), остаток уходит в минус и оценивается по последней цене. Когда проведёте закупку, минус погасится, а себестоимость проданного поправится автоматически. Списания и перемещения в минус по умолчанию запрещены." },
        { t: "tip", text: "Галочка «Только «мало» и «в минусе»» — быстрый список того, что пора заказать. А уведомление «Заканчивается на складе» придёт само (не чаще раза в 6 часов на продукт)." },
      ],
    },
    {
      id: "purchases",
      title: "Закупки: приход продуктов",
      summary: "Как провести закупку: поставщик, количество в удобных единицах, сумма, срок годности, оплата сразу или в долг.",
      path: "/purchases",
      feature: "core.inventory_basic",
      permission: "stock_documents.view",
      keywords: ["закупка", "приход", "накладная", "поставка", "приёмка"],
      related: ["suppliers", "stock-balances", "expiry", "costing"],
      body: [
        { t: "video", id: "purchase", title: "Закупка от поставщика", duration: 44 },
        {
          t: "steps",
          items: [
            "[«Закупки»](/purchases) → **«Новая закупка»**.",
            "Дата и поставщик (с дополнением «Закупки и поставщики» — из справочника, иначе просто текстом).",
            "С расширенным складом — **склад**, куда приходует товар.",
            "Строки: продукт (нет в списке — **«Добавить продукт «…»»** прямо из поиска или кнопка **«Новый продукт»**), количество, единица (кг/г, л/мл, шт), **потери** и **сумма строки** — цену за единицу система посчитает сама.",
            "С расширенным складом — **«Годен до»** для партии; пусто — по сроку хранения продукта.",
            "Для поставщика из справочника: **в долг** (со сроком оплаты), **наличными** (из кассовой смены), **переводом** или **смешанно**.",
            "**«Провести»** — остатки и цены продуктов обновятся сразу.",
          ],
        },
        { t: "warn", text: "Проведённый документ не редактируется. Ошибку исправляют удалением (сторно — обратные движения) и новой закупкой. Закупку, по которой уже есть оплаты или возвраты, сначала нужно освободить от них." },
        { t: "tip", text: "Закупка обновляет **последнюю цену продукта** — от неё считается себестоимость блюд в калькуляции." },
        { t: "tip", text: "**Потери при приёмке** (кости, очистка, разморозка) — в **%** или фиксированно в **кг/л/шт**. На склад придёт количество за вычетом потерь, сумма строки та же — цена за кг считается от чистого веса. Под строкой видно, сколько придёт на склад и почём." },
      ],
    },
    {
      id: "purchase-requests",
      title: "Заявки на закупку",
      summary: "Сотрудник пишет, что купить, с заметкой; управляющий одобряет, закупает по заявке или отказывает.",
      path: "/purchase-requests",
      feature: "core.inventory_basic",
      permission: "purchase_requests.create",
      keywords: ["заявка", "закупка", "купить", "заканчивается", "список покупок"],
      related: ["purchases", "stock-balances"],
      body: [
        { t: "p", text: "Повар, бармен или кассир замечает, что продукт заканчивается, и пишет заявку. Управляющий видит все заявки филиала, а сотрудник — только свои. В заявке видно, кто её написал и когда, кто одобрил, отказал или купил." },
        { t: "steps", title: "Сотруднику", items: [
          "[«Заявки на закупку»](/purchase-requests) → **«Новая заявка»**.",
          "Строки: продукт из справочника или просто название (**«Добавить «…» без справочника»**), количество, единица и заметка к строке — марка, жирность, размер.",
          "**Срочность**, **«Нужно к»** и общая **заметка** — зачем и к какому событию.",
          "Пока заявку не рассмотрели, её можно **изменить** или **отозвать**. О решении придёт уведомление.",
        ] },
        { t: "steps", title: "Управляющему", items: [
          "Баннер **«Ждут решения»** — новые заявки. Откройте заявку.",
          "**«Одобрить»** — с заметкой для автора, **«Отказать»** — с причиной.",
          "**«Оформить закупку»** — откроется новая закупка со строками из заявки; после проведения заявка сама станет «Куплено».",
          "**«Куплено»** — закрыть заявку без закупки на склад (например, купили хозтовары).",
        ] },
        { t: "p", text: "**«Что заказать»** — вкладка с подсказкой: по расходу за последние недели (продажи по техкартам, списания, производство) система считает расход в день, на сколько дней хватит остатка и сколько купить на выбранный срок с запасом — за вычетом того, что уже в заявках. Отметьте строки, поправьте количество и нажмите **«Создать заявку»** или **«Оформить закупку»**." },
        { t: "tip", text: "Права: **«Заявки на закупку — Создание»** — писать свои заявки (есть у повара, кассира и официанта), **«Просмотр»** — видеть все заявки филиала, **«Управление»** — решать по заявкам." },
      ],
    },
    {
      id: "writeoffs",
      title: "Списания: порча, просрочка, питание персонала",
      summary: "Как списать испорченные или использованные не для продажи продукты с причиной — и почему иногда нужен PIN менеджера.",
      path: "/writeoffs",
      feature: "core.inventory_basic",
      permission: "stock_documents.view",
      keywords: ["списание", "порча", "просрочка", "бой", "дегустация", "питание персонала"],
      related: ["stock-balances", "expiry", "pos-discounts", "fraud"],
      body: [
        { t: "steps", items: ["[«Списания»](/writeoffs) → **«Новое списание»**.", "Причина: порча, просрочка, ошибка приготовления, бой, питание персонала, дегустация, внутренние нужды, другое.", "Продукты и количество. Стоимость посчитается по партиям (ориентир по последней цене виден сразу).", "Провести. Больше остатка — нельзя."] },
        { t: "p", text: "Если сумма списания больше лимита вашей роли, касса попросит **PIN менеджера** — см. [[pos-discounts]]. Подозрительно крупные списания отмечает модуль [[fraud]]." },
      ],
    },
    {
      id: "inventory-counts",
      title: "Инвентаризация: пересчёт склада",
      summary: "Как пересчитать продукты, не останавливая продажи: снимок остатка, подсчёт, сверка и проведение разницы актом.",
      path: "/inventory-counts",
      feature: "core.inventory_basic",
      permission: "inventory_counts.view",
      keywords: ["инвентаризация", "пересчёт", "ревизия", "недостача", "излишек", "слепая"],
      related: ["stock-balances", "warehouses"],
      body: [
        { t: "video", id: "inventory-count", title: "Инвентаризация с недостачей", duration: 46 },
        {
          t: "steps",
          items: [
            "[«Инвентаризация»](/inventory-counts) → **«Начать инвентаризацию»**.",
            "Что считаем: **все продукты**, **выбранные** или, с расширенным складом, **место хранения** (например, «Холодильник №1»). Склад — если их несколько.",
            "**Слепая** — тот, кто считает, не видит остаток по системе (честнее подсчёт).",
            "Сотрудники вводят фактическое количество по строкам — в любых единицах. Можно считать вдвоём одновременно.",
            "Проверьте сверку: ожидаемый остаток, факт, разница и сумма. Фильтр «только расхождения».",
            "**«Провести»** — разница спишется (недостача) или оприходуется (излишек) актом инвентаризации.",
          ],
        },
        { t: "tip", text: "Продажи во время подсчёта учитываются: система сравнивает факт с остатком **на момент подсчёта строки**, а не начала инвентаризации. Останавливать кассу не нужно." },
      ],
    },
    {
      id: "suppliers",
      title: "Поставщики: долги, оплаты, авансы, возвраты, акт сверки",
      summary: "Справочник поставщиков с отсрочкой оплаты; сколько вы должны, оплаты из кассы или переводом, возвраты товара и акт сверки.",
      path: "/suppliers",
      feature: "addon.procurement",
      permission: "suppliers.view",
      keywords: ["поставщик", "долг поставщику", "оплата поставщику", "аванс", "акт сверки", "возврат поставщику", "отсрочка"],
      related: ["purchases", "expenses-finance"],
      body: [
        {
          t: "steps", title: "Поставщик и закупки в долг",
          items: [
            "[«Поставщики»](/suppliers) → **«Добавить поставщика»**: название, контакты, **отсрочка оплаты в днях**.",
            "Закупка от этого поставщика становится его **счётом**: долг растёт на сумму, срок оплаты = дата + отсрочка (можно перенести).",
            "Карточка поставщика: долг, закупки, оплаты, товары и цены, вкладка «Сколько мы должны».",
          ],
        },
        {
          t: "steps", title: "Оплата",
          items: [
            "В карточке — **«Оплатить»**: сумма, способ (наличные из смены кассы или перевод).",
            "Без выбора закупки оплата гасит счета по порядку сроков; можно указать конкретную закупку.",
            "**Аванс** — оплата больше долга: остаток сам зачтётся в будущие закупки.",
            "Ошиблись — оплату можно отменить.",
          ],
        },
        {
          t: "list", title: "Возвраты",
          items: [
            "**Возврат товара** — из карточки закупки «Вернуть поставщику» или в разделе [«Возвраты поставщикам»](/supplier-returns): товар уходит со склада по ценам закупки, долг уменьшается.",
            "**Возврат денег** — если поставщик вернул аванс: «Вернуть деньги» в карточке.",
          ],
        },
        { t: "tip", text: "**Акт сверки** за период печатается на A4 из карточки поставщика. Утром в день срока и при просрочке придёт уведомление «Оплата поставщикам»." },
      ],
    },
    {
      id: "warehouses",
      title: "Несколько складов, места хранения и настройки склада",
      summary: "Основной склад, кухня, бар, центральный склад; склад для станции кухни; полки и холодильники; правила для просрочки и полуфабрикатов.",
      path: "/warehouses",
      feature: "addon.inventory_pro",
      permission: "warehouses.view",
      keywords: ["склады", "кухня", "бар", "центральный склад", "место хранения", "холодильник", "полка", "настройки склада"],
      related: ["transfers", "stock-requests", "kds", "inventory-counts", "expiry"],
      body: [
        {
          t: "steps", title: "Склады филиала",
          items: [
            "[«Склады»](/warehouses) → **«Добавить склад»**: название и назначение (кухня, бар, центральный, прочий).",
            "**Основной** склад списывает продажи. Сделать основным другой — «⋯» → «Сделать основным».",
            "«Разрешить уход в минус» — списания и перемещения больше остатка не будут блокироваться.",
            "Удалить можно только пустой склад без перемещений в пути.",
          ],
        },
        { t: "p", text: "**Склад станции кухни.** В [«Кухонных станциях»](/kitchen-stations) у станции выберите склад — например «Бар». Позиции, отправленные на эту станцию, будут списываться с бара, остальные — с основного." },
        {
          t: "steps", title: "Места хранения",
          items: [
            "На карточке склада — **«Место»**: добавьте полки, холодильники, зоны.",
            "В [«Остатках»](/inventory) колонка **«Место»** — закрепите продукт за местом.",
            "Инвентаризацию можно провести по одному месту — см. [[inventory-counts]].",
          ],
        },
        {
          t: "terms", title: "Настройки склада (кнопка на странице «Склады»)",
          items: [
            { term: "Не продавать из просроченных партий", text: "Продажа берёт сначала свежие партии, просроченные — только если других нет. Они остаются на складе и ждут списания." },
            { term: "Предупреждать за N дней", text: "Порог «скоро истекает» для страницы сроков и утреннего уведомления." },
            { term: "Нет полуфабриката — списывать сырьём", text: "Если готового ПФ не хватает, продажа спишет сырьё по его ТТК. Выключите — ПФ уйдёт в минус, и будет видно, что его не доготовили." },
            { term: "Запас к прогнозу производства", text: "На сколько процентов делать больше среднего расхода — см. [[production]]." },
          ],
        },
      ],
    },
    {
      id: "transfers",
      title: "Перемещения между складами и филиалами",
      summary: "Склад → кухня проходит сразу; в другой филиал — «в пути» до приёмки. Товар переходит по себестоимости и со своими сроками.",
      path: "/transfers",
      feature: "addon.inventory_pro",
      permission: "stock_transfers.view",
      keywords: ["перемещение", "в пути", "приёмка", "отгрузка", "акт перемещения"],
      related: ["warehouses", "stock-requests"],
      body: [
        {
          t: "steps",
          items: [
            "[«Перемещения»](/transfers) → **«Новое перемещение»**: откуда (склад своего филиала) и куда (любой склад компании).",
            "Добавьте продукты и количество. Если товара не хватает, а склад не допускает минус — перемещение не пройдёт.",
            "Свой филиал — **«Переместить»**: остатки меняются сразу.",
            "Другой филиал — **«Отправить»**: товар уходит «в пути». Получатель видит баннер «Входящих перемещений» и нажимает **«Принять»**, указывая фактическое количество.",
            "Пришло меньше — **недостача** зафиксируется в перемещении. Отправитель может **отменить** перемещение, пока его не приняли.",
          ],
        },
        { t: "tip", text: "Кнопка **«Акт»** печатает акт перемещения на A4 с подписями «Отпустил» и «Принял»." },
      ],
    },
    {
      id: "stock-requests",
      title: "Заявки на центральный склад",
      summary: "Филиал заказывает товар у центрального склада, склад отгружает перемещением, филиал принимает — и заявка закрывается.",
      path: "/stock-requests",
      feature: "addon.inventory_pro",
      permission: "stock_transfers.view",
      keywords: ["заявка", "заказ на склад", "центральный склад", "внутренний заказ"],
      related: ["transfers", "warehouses"],
      body: [
        {
          t: "steps", title: "Филиал: заказать",
          items: ["[«Заявки на склад»](/stock-requests) → **«Новая заявка»**.", "«У какого склада» (обычно центральный), «На какой склад» и «Нужно к» (дата).", "Продукты и количество, комментарий. **«Отправить заявку»** — склад получит уведомление."],
        },
        {
          t: "steps", title: "Центральный склад: отгрузить",
          items: [
            "Баннер «Заявок к вашему складу» → откройте заявку. Видно, сколько товара у вас на складе.",
            "В колонке «Отгрузить» поправьте количество (можно меньше или 0) → **«Отгрузить»**. Создастся перемещение «в пути».",
            "Нет возможности — **«Отказать»** с причиной.",
          ],
        },
        { t: "p", text: "Когда филиал примет перемещение, заявка станет **«Получено»**. Если перемещение отменить — заявка снова «Ждёт отгрузки». Пока заявку не отгрузили, филиал может её **отозвать**." },
      ],
    },
    {
      id: "expiry",
      title: "Сроки годности",
      summary: "Просроченные и истекающие партии по складам, списание в один клик и утреннее уведомление.",
      path: "/expiry",
      feature: "addon.inventory_pro",
      permission: "stock.view",
      keywords: ["срок годности", "просрочка", "истекает", "fefo", "партия"],
      related: ["purchases", "writeoffs", "warehouses"],
      body: [
        { t: "p", text: "Срок у партии появляется при закупке («Годен до») или сам — дата закупки + срок хранения продукта. Партии полуфабрикатов получают срок при выпуске." },
        { t: "list", items: ["Карточки сверху: **просрочено** и **истекает за N дней** — с суммой. Нажмите — список отфильтруется.", "**«Списать»** у партии — списание с причиной «просрочка» в один клик.", "Каждое утро — уведомление «Сроки годности» в приложении и Telegram."] },
        { t: "tip", text: "Порог N дней и правило «не продавать просроченное» — в настройках склада, см. [[warehouses]]." },
      ],
    },
    {
      id: "conversions",
      title: "Разделка и сборка",
      summary: "Туша → вырезка, мякоть, кости; несколько товаров → подарочный набор. Себестоимость входа делится между выходами.",
      path: "/conversions",
      feature: "addon.inventory_pro",
      permission: "stock_documents.view",
      keywords: ["разделка", "туша", "разукомплектация", "сборка", "набор", "доли"],
      related: ["stock-balances", "costing"],
      body: [
        {
          t: "steps", title: "Разделка",
          items: [
            "[«Разделка и сборка»](/conversions) → **«Разделка»**.",
            "«Что разделываем» — один продукт и количество (например, туша 100 кг).",
            "«Что получилось» — части и их вес.",
            "**Доли себестоимости**: включите и укажите % для каждой части (сумма 100%). Без долей — по цене закупки частей, а если её нет — по весу.",
            "Внизу — оценка, сколько будет стоить каждая часть. **«Провести»**.",
          ],
        },
        { t: "p", text: "**Сборка** — наоборот: несколько продуктов на входе, один на выходе; его себестоимость = сумма входов." },
        { t: "tip", text: "Сохраните **шаблон** («Туша говяжья» с долями) — в следующий раз форма заполнится одним нажатием. Отменить операцию — откройте её → «Отменить» (если части ещё не израсходованы)." },
      ],
    },
    {
      id: "production",
      title: "Производство полуфабрикатов",
      summary: "План на день, выпуск ПФ по техкарте с точной себестоимостью, сроком годности и этикетками; прогноз, сколько готовить.",
      path: "/production",
      feature: "addon.production",
      permission: "production.view",
      keywords: ["производство", "полуфабрикат", "пф", "тесто", "заготовка", "выпуск", "этикетка", "план", "прогноз"],
      related: ["recipes", "products", "warehouses"],
      body: [
        {
          t: "steps", title: "Подготовка",
          items: ["Создайте продукт типа **«Полуфабрикат»** (например, тесто) с **сроком хранения**.", "Сделайте его техкарту на выход, например 1000 г: мука 600 г, вода 380 мл… См. [[recipes]].", "Используйте ПФ в техкартах блюд (пицца — 250 г теста)."],
        },
        {
          t: "steps", title: "Работа",
          items: [
            "[«Производство»](/production) → **«Запланировать производство»**: ПФ, сколько сделать, дата. Сразу видно сырьё, остатки и нехватку.",
            "Или **«Предложить план»**: система посчитает по продажам того же дня недели за 4 недели + запас − остаток − уже запланированное. Отметьте нужное → «Запланировать».",
            "Повар нажимает **«Начать»**, после готовки — **«Провести выпуск»**: фактический выход и срок годности. Если сырья ушло не по ТТК — «Расход отличался от техкарты».",
            "Печать **этикеток** (название, дата, срок, повар) — в карточке выпуска.",
          ],
        },
        {
          t: "list", title: "Что считается",
          items: ["Сырьё списывается со склада сырья, ПФ приходует на склад готового партией со сроком.", "**Себестоимость ПФ = стоимость сырья ÷ фактический выход** — она же идёт в себестоимость блюд.", "При продаже пиццы спишется готовое тесто, а не мука.", "Вкладка «Отчёт»: план, факт, отклонение выхода, себестоимость."],
        },
        { t: "warn", text: "Отменить выпуск можно, пока ПФ не израсходован (сырьё вернётся на склад)." },
      ],
    },
  ],
};
